import i18n from "locales/i18n";
import {
  enumLevelTrd,
  enumsCreateUserWorkgroup,
  enumsTypeStatusUser,
  enumsTypeWorkflowStep,
  enumsWorkflowStatus,
} from "./enums";
import { isNullOrUndefined } from "./validations";

/**
 * Sorts an array of objects based on a specified property name in a case-insensitive manner.
 * @param {Array} list - The array to be sorted.
 * @param {string} propertyName - The property name to use for sorting.
 * @returns {Array} The sorted array.
 */
export const sortByPropertyName = (list, propertyName) => {
  return list.sort((firstItem, secondItem) => {
    const firstProperty = `${firstItem[propertyName]}`.toLowerCase();
    const secondProperty = `${secondItem[propertyName]}`.toLowerCase();
    return firstProperty.localeCompare(secondProperty);
  });
};

/**
 * Sorts the users list by their full names in descending order and sets the dynamic users state.
 * @param {Array} usersList - The list of users to be sorted and processed.
 * @param {Function} setUsersDinamic - The state setter function for dynamic users.
 */
export const processSortAndSetUsers = (usersList, setUsersDinamic) => {
  if (!Array.isArray(usersList) || usersList.length === 0) return;

  const filteredAndSortedUsers = usersList
    .filter(
      (user) =>
        user.status !== enumsTypeStatusUser.RETIRED && user.status !== enumsTypeStatusUser.INACTIVE,
    )
    .sort((a, b) => {
      const nameA = `${a.name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.name} ${b.last_name}`.toLowerCase();
      return nameB.localeCompare(nameA);
    })
    .map((user) => ({
      value: user.uuid,
      label: `${user.first_name} ${user.last_name} (${user.user_name})`,
    }));

  setUsersDinamic(filteredAndSortedUsers);
};

export const sortAndFilterGroups = (groupList, setUsersDinamic) => {
  if (!Array.isArray(groupList) || groupList.length === 0) return;

  const filteredAndSortedGroups = groupList
    .filter((group) => group.status === enumsCreateUserWorkgroup.STATUS_ACTIVE)
    .sort((positionNameA, positionNameB) => {
      const nameA = positionNameA.name.toLowerCase();
      const nameB = positionNameB.name.toLowerCase();
      return nameB.localeCompare(nameA);
    })
    .map((group) => ({
      value: group.uuid,
      label: group.name,
    }));

  setUsersDinamic(filteredAndSortedGroups);
};

/**
 * Sorts and filters active workflows from a list and updates the dynamic workflows state.
 * @param {Array} listWorkflows - The list of workflows to be filtered and sorted.
 * @param {Function} setWorkflowsDinamic - The function to update the dynamic workflows state.
 * @returns {void} Returns nothing.
 */
export const sortActiveWorkflows = (listWorkflows, setWorkflowsDinamic) => {
  if (!Array.isArray(listWorkflows) || listWorkflows.length === 0) return;

  const filteredWorkfloiws = listWorkflows
    .filter((workflow) => workflow.status === enumsWorkflowStatus.ACTIVE)
    .sort((positionNameA, positionNameB) => {
      const nombresA = positionNameA.name.toLocaleLowerCase();
      const nombresB = positionNameB.name.toLocaleLowerCase();
      return nombresA.localeCompare(nombresB);
    })
    .map((workflow) => ({
      value: workflow.uuid,
      label: workflow.name,
    }));

  setWorkflowsDinamic(filteredWorkfloiws);
};

/**
 * Sorts and filters workflow steps, then updates the dynamic workflow steps.
 * @param {Array} listWorkflowSteps - The list of workflow steps to be sorted and filtered.
 * @param {Function} setWorkflowsStepsDinamic - The function to update the dynamic workflow steps.
 */
export const sortWorkflowsSteps = (listWorkflowSteps, setWorkflowsStepsDinamic) => {
  if (!Array.isArray(listWorkflowSteps) || listWorkflowSteps.length === 0) return;

  const filteredWorkfloiwSteps = listWorkflowSteps
    .filter(
      (step) =>
        step.type !== enumsTypeWorkflowStep.EXCLUSIVE_GATEWAY &&
        step.type !== enumsTypeWorkflowStep.END_EVENT,
    )
    .map((step, index) => ({
      value: step.uuid,
      label: `${i18n.t("taskFilter.label17")} ${index + 1}  : ${step.name}`,
    }));

  setWorkflowsStepsDinamic(filteredWorkfloiwSteps);
};

/**
 * Sorts and filters a list of items by status and name.
 * @param {Array} list - The list of items to be filtered and sorted.
 * @param {string} activeStatus - The status to filter by.
 * @returns {Array} - The sorted and filtered list.
 */
export function sortByNameAndStatus(list, filterStatus) {
  return list
    .filter((item) => item.status === filterStatus)
    .sort((firstPropertie, secondPropertie) => {
      const firsItem = firstPropertie.name.toLocaleLowerCase();
      const secondItem = secondPropertie.name.toLocaleLowerCase();
      return firsItem.localeCompare(secondItem);
    });
}

/**
 * Sorts the list of steps alphabetically by their names and sets the sorted steps dynamically.
 * @param {Array<Object>} stepsList - The list of step objects to be sorted. Each step object should have `step_name` and `uuid` properties.
 * @param {Function} setStepsToReturnList - The state setter function used to update the steps dynamically. It receives the sorted steps with `value` (UUID)
 * and `label` (step name) properties.
 */
export const sortStepsList = (stepsList, setStepsToReturnList) => {
  stepsList
    .sort((positionNameA, positionNameB) => {
      const nombresA = positionNameA.step_name.toLocaleLowerCase();
      const nombresB = positionNameB.step_name.toLocaleLowerCase();
      return nombresA.localeCompare(nombresB);
    })
    .forEach((step) => {
      setStepsToReturnList((prevState) => [
        ...prevState,
        {
          value: step.uuid,
          label: step.step_name,
        },
      ]);
    });
};

/**
 * Sorts the list of users alphabetically by their full names and sets the sorted users dynamically.
 * @param {Array<Object>} listUsers - The list of user objects to be sorted. Each user object should have `name`, `last_name`, `uuid`, `first_name`, and `user_name` properties.
 * @param {Function} setUsersDinamicProcess - The state setter function used to update the users dynamically. It receives the sorted users with
 * `value` (UUID) and `label` (full name and username) properties.
 */
export const sortUserByProcess = (listUsers, setUsersDinamicProcess) => {
  listUsers
    .sort((positionNameA, positionNameB) => {
      const nombresA = `${positionNameA.name} ${positionNameA.last_name} `.toLocaleLowerCase();
      const nombresB = `${positionNameB.name} ${positionNameB.last_name} `.toLocaleLowerCase();
      return nombresA.localeCompare(nombresB);
    })
    .forEach((user) => {
      setUsersDinamicProcess((prevState) => [
        ...prevState,
        {
          value: user.uuid,
          label: `${user.first_name} ${user.last_name} (${user.user_name})`,
        },
      ]);
    });
};

/**
 * Sorts the forms alphabetically by their names and sets the sorted forms dynamically.
 * @param {Array<Object>} forms - The list of form objects to be sorted. Each form object should have at least a `name` and `uuid` property.
 * @param {Function} setFormsDinamic - The state setter function used to update the forms dynamically. It receives the sorted forms with `value` and `label` properties.
 */
export const sortActiveForms = (forms, setFormsDinamic) => {
  const sortedForms = forms.sort((positionNameA, positionNameB) => {
    const nombresA = `${positionNameA.name} `.toLocaleLowerCase();
    const nombresB = `${positionNameB.name} `.toLocaleLowerCase();
    return nombresA.localeCompare(nombresB);
  });
  setFormsDinamic(
    sortedForms.map((item) => ({
      value: item.uuid,
      label: item.name,
    })),
  );
};

/**
 * Sorts the list of workflows alphabetically by their names and sets the sorted workflows in a dynamic state.
 * @param {Array<Object>} listWorkflows - The list of workflow objects to be sorted. Each workflow object should have at least a `name` and `uuid` property.
 * @param {Function} setWorkflowsDinamic - The state setter function used to update the workflows dynamically.
 * It receives the sorted workflows with `value` and `label` properties.
 */
export const sortAllWorkflows = (listWorkflows, setWorkflowsDinamic) => {
  listWorkflows
    .sort((firstName, secondName) => {
      const nombresA = `${firstName.name} `.toLocaleLowerCase();
      const nombresB = `${secondName.name} `.toLocaleLowerCase();
      return nombresA.localeCompare(nombresB);
    })
    .forEach((workflow) => {
      if (workflow) {
        setWorkflowsDinamic((prevState) => [
          ...prevState,
          {
            value: workflow.uuid,
            label: workflow.name,
          },
        ]);
      }
    });
};

/* Filters and sorts the structureTrd data.
 * @param {Array} structureTrd - The array of nodes to be filtered and sorted.
 * @returns {Array} The filtered and sorted data.
 */
export const sortDataStructureTrd = (structureTrd) => {
  if (isNullOrUndefined(structureTrd) === true) {
    return [];
  }

  const backgrounds = structureTrd.filter((node) => node.level === enumLevelTrd.BACKGROUND);
  const sortedLevels = structureTrd
    .filter((node) => node.level > enumLevelTrd.BACKGROUND)
    .sort((firstNodes, secondNodes) => firstNodes.code.localeCompare(secondNodes.code));

  return [...backgrounds, ...sortedLevels];
};

/**
 * Sorts an array of objects by a given property and maps it to a value/label structure.
 * @param {Array<Object>} list - The array to be sorted.
 * @param {string} propertyName - The object property used for sorting.
 * @returns {Array<{ value: string, label: string }>|undefined}
 * Returns a sorted and mapped array, or undefined if the input list is invalid or empty.
 */
export const sortGeneralArray = (list, propertyName) => {
  if (!Array.isArray(list) || list.length === 0) return;
  const sortedList = list
    .sort((first, second) => {
      const nameA = first[propertyName].toLowerCase();
      const nameB = second[propertyName].toLowerCase();
      return nameA.localeCompare(nameB);
    })
    .map((element) => ({
      value: element.uuid,
      label: element.name,
    }));

  return sortedList;
};
